import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Alert } from "reactstrap";

import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { totalRewardSettings } from "src/utils/RoleBasedSecurity/permissions";
import Form from "src/Components/Form/index";
import ContentLoading from "src/Components/Loading/contentLoading";
import { useCompanyId } from "src/Pages/Company/CompanyContext";

import {
  useListTotalRewardsConfigs,
  useListTotalRewardsConfigsDictionary,
  useSaveTotalRewardsConfig,
} from "src/Pages/Company/TotalReward/api/useConfigs";
import useCompanyBenefits from "src/Pages/Company/TotalReward/api/useCompanyBenefits";
import useCompanyPointsBanks from "src/Pages/Company/TotalReward/api/useCompanyPointsBanks";
import useCompanySubscriptions from "src/Pages/Company/TotalReward/api/useCompanySubscriptions";
import { isEmpty, omit, orderBy, uniq, update } from "lodash";
import moment from "moment";

const parseValue = (value, type) => {
  if (type === "boolean") {
    return value === "1";
  }
  if (type === "autocompleteMultiselect") {
    return value.split(",").filter(Boolean);
  }
  return value;
};

export default function TotalRewardSettingsForm() {
  const companyId = useCompanyId();

  const {
    data: functionalities,
    isLoading: loadingFunctionalities,
    isSuccess: successFunctionalities,
    isError: errorFunctionalities,
  } = useListTotalRewardsConfigsDictionary(companyId);

  const defaultValues = useMemo(
    () =>
      functionalities?.reduce(
        (defaultValues, item) => ({
          ...defaultValues,
          ...(item.defaultValue !== null
            ? { [item.key]: parseValue(item.defaultValue, item.type) }
            : {}),
        }),
        {}
      ),
    [functionalities]
  );

  const hasPointBanks = functionalities?.some(
    ({ source_options }) => source_options === "point_banks"
  );
  const hasBenefits = functionalities?.some(
    ({ source_options }) => source_options === "benefits"
  );

  const {
    data: pointBanks,
    isLoading: loadingPointBanks,
    isSuccess: successPointBanks,
  } = useCompanyPointsBanks(companyId, !!hasPointBanks, true);

  const {
    data: benefits,
    isLoading: loadingBenefits,
    isSuccess: successBenefits,
  } = useCompanyBenefits(companyId, !!hasBenefits, true);

  const {
    data: subscriptions,
    isLoading: loadingSubscriptions,
    isSuccess: successSubscriptions,
  } = useCompanySubscriptions(companyId, !!hasBenefits, true);

  const getOptions = useCallback(
    (type) => {
      if (type === "point_banks") {
        return pointBanks;
      }
      if (type === "benefits") {
        return orderBy(
          uniq([...(benefits || []), ...(subscriptions || [])]),
          "label"
        );
      }
      return [];
    },
    [benefits, pointBanks, subscriptions]
  );

  return (
    <ContentLoading
      show={
        loadingFunctionalities ||
        loadingPointBanks ||
        loadingBenefits ||
        loadingSubscriptions
      }
    >
      {errorFunctionalities && (
        <Alert color="danger">
          {__("Nie udało się pobrać definicji konfiguracji modułu.")}{" "}
          {__(
            "Spróbuj ponownie za chwilę lub skontaktuj się z administratorem."
          )}
        </Alert>
      )}
      {successFunctionalities && (
        <SettingsForm
          functionalities={functionalities}
          defaultValues={defaultValues}
          getOptions={getOptions}
        />
      )}
    </ContentLoading>
  );
}

TotalRewardSettingsForm.propTypes = {};

const SettingsForm = ({ functionalities, defaultValues, getOptions }) => {
  const companyId = useCompanyId();

  const {
    data: config,
    isLoading: loadingConfig,
    isError: errorConfig,
  } = useListTotalRewardsConfigs(companyId);

  const configValues = useMemo(() => {
    const formValues = config?.reduce(
      (formValues, item) => ({
        ...formValues,
        [item.configKey]: parseValue(
          item.configValue,
          functionalities?.find((f) => f.key === item.configKey)?.type
        ),
      }),
      {}
    );

    return { ...defaultValues, ...formValues };
  }, [config, defaultValues, functionalities]);

  const [data, setData] = useState({});

  useEffect(() => {
    setData(configValues);
  }, [configValues]);

  const [apiErrors, setApiErrors] = useState({});

  const onChange = (key, value) => {
    setApiErrors((prev) => omit(prev, [key]));
    setData((prevData) => {
      const updatedData = { ...prevData };
      update(updatedData, key, (v) => value);
      return updatedData;
    });
  };

  const formElements = useMemo(
    () =>
      functionalities.map((f) => ({
        id: f.key,
        label: f.title,
        type: f.type,
        depends: f.depends,
        options: ["select", "multiselect", "autocompleteMultiselect"].includes(
          f.type
        )
          ? f.options || getOptions(f.source_options)
          : undefined,
        tooltip: ["multiselect"].includes(f.type)
          ? {
              content: `${__(
                "Przytrzymaj klawisz CTRL aby zaznaczyć kilka opcji"
              )}`,
            }
          : undefined,
        props: ["autocompleteMultiselect"].includes(f.type)
          ? { closeMenuOnSelect: false }
          : undefined,
        minDate:
          ["date"].includes(f.type) && f.minDate
            ? new Date(f.minDate)
            : undefined,
        maxDate:
          ["date"].includes(f.type) && f.maxDate
            ? new Date(f.maxDate)
            : undefined,
        ...((f.required ||
          (["date"].includes(f.type) && (f.minDate || f.maxDate))) && {
          validation: [
            ...(f.required ? ["required"] : []),
            ...(["date"].includes(f.type) && f.minDate
              ? [
                  {
                    method: "greaterEqualThanDate",
                    args: [f.minDate],
                  },
                ]
              : []),
            ...(["date"].includes(f.type) && f.maxDate
              ? [
                  {
                    method: "lessEqualThanDate",
                    args: [f.maxDate],
                  },
                ]
              : []),
          ],
        }),
        ...(apiErrors[f.key] && { errorMessage: apiErrors[f.key] }),
      })),
    [apiErrors, functionalities, getOptions]
  );

  const mutation = useSaveTotalRewardsConfig(companyId);

  const saveConfig = async (data) => {
    const parseValue = (value, type) => {
      if (type === "date") {
        return value ? moment(value).format("YYYY-MM-DD") : "";
      }
      if (type === "boolean") {
        return value ? "1" : "0";
      }
      return String(value);
    };

    setApiErrors({});

    // WARNIGN: API does not allow you to send a form in one request
    const mutateResult = await Promise.allSettled(
      functionalities
        .map((f) => {
          let configValue = parseValue(data[f.key], f.type);
          if (f.depends?.field) {
            const dependsOn = functionalities.find(
              (ff) => ff.key === f.depends.field
            );
            if (dependsOn && data[dependsOn.key] !== f.depends.value) {
              configValue = null;
            }
          }
          const currentConfig = config.find((c) => c.configKey === f.key);
          return {
            id: currentConfig?.id,
            tenant: currentConfig?.tenant,
            configKey: f.key,
            configValue,
            valueType: f.type,
          };
        })
        .filter((payload) => payload.id || payload.configValue !== null)
        .filter(
          (payload) =>
            payload.configValue !==
            config.find((c) => c.id === payload.id)?.configValue
        )
        .map((payload) =>
          mutation.mutateAsync(payload, {
            onError: (error, variables, context) => {
              setApiErrors((prev) => ({
                ...prev,
                [variables.configKey]: error?.message,
              }));
            },
          })
        )
    );

    if (mutateResult.some((result) => result.status === "rejected")) {
      dynamicNotification(
        __("Popraw błędy i spróbuj ponownie."),
        "error",
        undefined,
        10000
      );
    } else {
      dynamicNotification(__("Zapisano konfigurację."));
    }
  };

  return (
    <ContentLoading
      show={loadingConfig || mutation.isPending}
      backgroundStyle={{ backgroundColor: "rgba(0, 0, 0, 0.2)" }}
    >
      {errorConfig && (
        <Alert color="danger">
          {__("Nie udało się pobrać konfiguracji modułu.")}{" "}
          {__(
            "Spróbuj ponownie za chwilę lub skontaktuj się z administratorem."
          )}
        </Alert>
      )}
      <Form
        id="totalRewardPerkSettings"
        data={data}
        config={{
          title: `${__("Konfigurowanie modułu")}`,
          stickyTitle: true,
          onSubmit: () => saveConfig(data),
          defaultOnChange: onChange,
          buttons: [
            {
              id: "settingsSubmit",
              size: "lg",
              color: "primary",
              className: "me-2",
              text: `${__("Zapisz")}`,
              type: "submit",
              disabled: mutation.isPending,
              permission: totalRewardSettings,
            },
          ],
          formGroups: [
            {
              formElements,
            },
          ],
        }}
      />
    </ContentLoading>
  );
};

SettingsForm.propTypes = {
  defaultValues: PropTypes.arrayOf(
    PropTypes.shape({
      configKey: PropTypes.string,
      configValue: PropTypes.string,
    })
  ).isRequired,
  functionalities: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      key: PropTypes.string,
      type: PropTypes.string,
      defaultValue: PropTypes.any,
    })
  ).isRequired,
  getOptions: PropTypes.func.isRequired,
};
